import { useNavigate } from 'react-router-dom';
import { Box, Chip, Grid, TableCell, TableRow } from '@mui/material';
import { FC, Fragment, Reducer, useCallback, useEffect, useReducer } from 'react';
// components
import { Alert } from 'components/common/Alert';
import TableLoader from 'components/common/TableLoader';
import NoDataFound from 'components/common/NoDataFound';
import TableContainer from 'components/common/TableContainer';
import TableComponent from 'components/common/TableComponent';
import TableSearchField from 'components/common/TableSearchField';
import TableTabsComponent from 'components/common/TableTabsComponent';
import ActionMenuDropdown from 'components/common/ActionMenuDropdown';
import ProductTypesSelect from 'components/common/simpleSelectors/ProductType';
import ExcipientLabelSelect from 'components/common/simpleSelectors/ExcipientLabel';
// interfaces, constants, reducers, graphql, styles
import { SelectType } from 'interfaces';
import { cursorPointer, textWhiteSpace } from 'styles/commonComponentStyle';
import { Action, ActionType, State, initialState, excipientTypeReducer } from 'reducer/excipientTypeReducer';
import {
  TABS,
  TabEnum,
  ACTIVE_TEXT,
  HTTP_STATUS,
  INACTIVE_TEXT,
  PRODUCT_TYPE_TEXT,
  LOADING_TABLE_ROWS,
  EXCIPIENT_LABEL_TEXT,
  PRODUCT_EXCIPIENT_ROUTE,
  EDIT_EXCIPIENT_TYPES_ROUTE,
  EXCIPIENT_TYPE_TABLE_HEADER,
} from 'constants/index';
import {
  ExcipientTypesPayload,
  useUpdateExcipientTypeMutation,
  useFindAllExcipientTypesLazyQuery,
} from 'generated/graphql';

const ExcipientTable: FC = () => {
  const [state, dispatch] = useReducer<Reducer<State, Action>>(excipientTypeReducer, initialState);
  const { page, rowsPerPage, search, count, data, activeTab, productType, excipientLabel } = state;

  const { value: productTypeId } = productType;
  const { value: excipientLabelId } = excipientLabel;

  const navigate = useNavigate();

  const [findAllExcipientTypes, { loading: fetchLoading, error }] = useFindAllExcipientTypesLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllExcipientTypes } = data;
      const { pagination, response, data: excipientData } = findAllExcipientTypes || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { page, totalCount } = pagination || {};
        dispatch({ type: ActionType.SET_PAGE, page: page || 1 });
        dispatch({ type: ActionType.SET_COUNT, count: totalCount || 0 });
        dispatch({ type: ActionType.SET_DATA, data: excipientData as ExcipientTypesPayload['data'] });
      } else {
        resetPage();
      }
    },

    onError: () => {
      resetPage();
    },
  });

  const [updateExcipientTypeStatus, { loading: updateLoading }] = useUpdateExcipientTypeMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { updateExcipientType } = data;
      const { response } = updateExcipientType || {};
      const { message, status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        fetchExcipientTypes();
      } else {
        Alert.error(message ?? '');
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const resetPage = () => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_COUNT, count: 0 });
    dispatch({ type: ActionType.SET_DATA, data: [] });
  };

  const fetchExcipientTypes = useCallback(async () => {
    await findAllExcipientTypes({
      variables: {
        findAllExcipientTypesInput: {
          search,
          paginationOptions: {
            page,
            limit: rowsPerPage,
          },
          ...(productTypeId && { productTypeId }),
          ...(excipientLabelId && { excipientLabelId }),
          ...(activeTab !== TabEnum.All && { isActive: activeTab === TabEnum.Active }),
        },
      },
    });
  }, [findAllExcipientTypes, search, rowsPerPage, page, activeTab, productTypeId, excipientLabelId]);

  useEffect(() => {
    (!search.length || search.length > 2) && fetchExcipientTypes();
  }, [fetchExcipientTypes, search.length]);

  const tabHandler = (_: React.SyntheticEvent<Element, Event>, value: string) => {
    dispatch({ type: ActionType.SET_ACTIVE_TAB, activeTab: value as TabEnum });
  };

  const onActiveHandler = async (id: string, status: boolean) => {
    await updateExcipientTypeStatus({
      variables: {
        updateExcipientTypeInput: {
          id,
          isActive: !status,
        },
      },
    });
  };

  const onProductTypeChange = useCallback((item: SelectType) => {
    dispatch({ type: ActionType.SET_PRODUCT_TYPE, productType: item });
  }, []);

  const onExcipientLabelChange = useCallback((item: SelectType) => {
    dispatch({ type: ActionType.SET_EXCIPIENT_LABEL, excipientLabel: item });
  }, []);

  const loading = fetchLoading || updateLoading;

  const noData = Boolean((!fetchLoading && data?.length === 0) || error);

  return (
    <TableContainer>
      <TableTabsComponent activeTab={activeTab} tabsList={TABS} tabHandler={tabHandler} />
      <Box m={({ spacing }) => spacing(2, 3)}>
        <Grid container spacing={2}>
          <Grid item lg={4} md={6} sm={12} xs={12}>
            <TableSearchField
              search={search}
              withSpacing={false}
              onClear={() => dispatch({ type: ActionType.SET_SEARCH, search: '' })}
              onChange={(val) => dispatch({ type: ActionType.SET_SEARCH, search: val })}
            />
          </Grid>
          <Grid item lg={4} md={6} sm={12} xs={12}>
            <ProductTypesSelect
              isClearable
              value={productType}
              name="product-type"
              title={PRODUCT_TYPE_TEXT}
              handleChange={onProductTypeChange}
            />
          </Grid>

          <Grid item lg={4} md={6} sm={12} xs={12}>
            <ExcipientLabelSelect
              isClearable
              value={excipientLabel}
              name="excipient-label"
              title={EXCIPIENT_LABEL_TEXT}
              handleChange={onExcipientLabelChange}
            />
          </Grid>
        </Grid>
      </Box>

      <TableComponent
        noData={noData}
        page={page - 1}
        count={count}
        rowsPerPage={rowsPerPage}
        tableHeader={EXCIPIENT_TYPE_TABLE_HEADER}
        setPage={(p: number) => dispatch({ type: ActionType.SET_PAGE, page: p + 1 })}
        setRowsPerPage={(r: number) => dispatch({ type: ActionType.SET_ROWS_PER_PAGE, rowsPerPage: r })}>
        {loading ? (
          <TableLoader columns={8} rows={LOADING_TABLE_ROWS} />
        ) : (
          <Fragment>
            {data?.map((cell) => {
              const { id, name, excipientLabel, loadAdd, loadAddUnit, perKgCost, isActive, productType } =
                cell || {};
              const { name: excipientLabelName } = excipientLabel || {};
              const { name: productTypeName } = productType || {};

              return (
                <TableRow key={id}>
                  <TableCell sx={cursorPointer} onClick={() => navigate(`${PRODUCT_EXCIPIENT_ROUTE}/${id}`)}>
                    <b>{name ?? '--'}</b>
                  </TableCell>
                  <TableCell sx={textWhiteSpace}>{productTypeName ?? '--'}</TableCell>
                  <TableCell sx={textWhiteSpace}>{excipientLabelName ?? '--'}</TableCell>
                  <TableCell sx={textWhiteSpace}>{perKgCost ? `$${perKgCost}` : '--'}</TableCell>
                  <TableCell sx={textWhiteSpace}>{loadAdd ?? '--'}</TableCell>
                  <TableCell sx={textWhiteSpace}>{loadAddUnit ?? '--'}</TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={isActive ? ACTIVE_TEXT : INACTIVE_TEXT}
                      color={isActive ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell>
                    <ActionMenuDropdown
                      id={id ?? ''}
                      isActive={!!isActive}
                      editRoute={EDIT_EXCIPIENT_TYPES_ROUTE}
                      onActiveHandler={onActiveHandler}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </Fragment>
        )}
      </TableComponent>

      <NoDataFound noData={noData} />
    </TableContainer>
  );
};

export default ExcipientTable;
